import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronUp,
  MessageCircle,
  Search,
  Upload,
  Edit2,
  X,
  Trash2,
  Star,
} from "lucide-react";
import { UpdatePreferenceNegotiable, PutUpdatePhotoStatus } from "./apis/put";
import { GetCasteList, GetCustomerSupportAdmins, GetUserWhatsappChat } from "./apis/get";
import { PatchAssignUserInstruction } from "./apis/patch";
import { PostSetCommunity, PostUploadBiodata, PostUserWhatsappChatMessage } from "./apis/post";
import { DeleteUserPhotos } from "./apis/delete";
import toast from "react-hot-toast";
import LoaderService from "../services/LoaderService";
import ChatInput from "./Whatsapp/ChatInput";
import ChatWindow from "./Whatsapp/ChatWindow";

interface IndividualProfileViewProps {
  profile: any;
  preference: any;
  photos: any[];
  biodata: string;
  preferenceCategories?: any[];
}

const IndividualProfileView: React.FC<IndividualProfileViewProps> = ({
  profile,
  preference,
  photos,
  biodata,
  preferenceCategories = [],
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<
    "profile" | "preference" | "photos" | "biodata" | "chat"
  >("profile");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [openEditDropdown, setOpenEditDropdown] = useState<string | null>(null);
  const [pendingSelections, setPendingSelections] = useState<
    Record<string, any[]>
  >({});
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    contact: false,
    professional: false,
    family: false,
    lifestyle: false,
    children: false,
    about: true,
  });
  const FALLBACK_CASTE_LIST = [
    "Agarwal",
    "Aggarwal",
    "Bansal",
    "Dad",
    "Goyal",
    "Garg",
    "Gupta",
    "Jain",
    "Khandelwal",
    "Maheshwari",
    "Marwari",
    "Mittal",
    "Singhal",
    "Sharma",
    "Verma",
    "Rastogi",
    "Jindal",
    "Tayal",
    "Sachdeva",
    "Bajaj",
    "Mangal",
    "Mundra",
    "Porwal",
    "Bhansali",
    "Oswal",
    "Kothari",
    "Lahoti",
    "Modi",
    "Nathani",
    "Rathi",
    "Saxena",
    "Sethi",
    "Biyani",
    "Chordia",
    "Dalmia",
    "Didwania",
    "Gattani",
    "Kabra",
    "Kedia",
    "Lodha",
    "Somani",
    "Surana",
  ];

  const [casteList, setCasteList] = useState<string[]>(FALLBACK_CASTE_LIST);
  const [casteDropdownOpen, setCasteDropdownOpen] = useState(false);
  const [casteSearchTerm, setCasteSearchTerm] = useState("");

  const [supportAdmins, setSupportAdmins] = useState<any[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedPhotoUrls, setSelectedPhotoUrls] = useState<string[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [photosToDelete, setPhotosToDelete] = useState<string[]>([]);
  const [deleteModalMessage, setDeleteModalMessage] = useState("");
  const [whatsappChatLoading, setWhatsappChatLoading] = useState(false);
  const [whatsappOlderLoading, setWhatsappOlderLoading] = useState(false);
  const [whatsappMessageSending, setWhatsappMessageSending] = useState(false);
  const [whatsappChatData, setWhatsappChatData] = useState<any | null>(null);
  const [chatPreviewImage, setChatPreviewImage] = useState<string | null>(null);
  const whatsappChatScrollRef = useRef<HTMLDivElement>(null);
  const shouldScrollWhatsappBottomRef = useRef(false);

  const userId = profile?.UserId || profile?.userId || profile?.user_id;
  const galleryPhotos = Array.isArray(photos) ? photos : [];
  const whatsappMessages = ((whatsappChatData?.messages || []) as any[]).map(
    (message) => ({
      ...message,
      text: message.body || message.caption || "",
      type: message.message_type || "text",
      message_type: message.message_type,
      template_name: message.template_name,
      template_language: message.template_language,
      template_variables: message.template_variables,
      sender:
        message.sender === "user" || message.direction === "inbound"
          ? "user"
          : "admin",
      timestamp: message.sent_at || message.created_at || message.updated_at,
      sent_at: message.sent_at || message.created_at || message.updated_at,
      _id: message.id ? String(message.id) : message.message_id,
      is_read: message.is_read,
      is_delivered: message.is_delivered,
    }),
  );

  const getPhotoUrl = (photo: any) =>
    photo?.url || photo?.photo_url || photo?.profile_pic_url || photo?.image || "";

  const isPrimaryPhoto = (photo: any) =>
    Boolean(photo?.profile || photo?.is_profile || photo?.primary || photo?.is_primary);

  const togglePhotoSelection = (photoUrl: string) => {
    if (!photoUrl) return;

    setSelectedPhotoUrls((current) =>
      current.includes(photoUrl)
        ? current.filter((url) => url !== photoUrl)
        : [...current, photoUrl],
    );
  };

  const openDeleteConfirmation = (photoUrls: string[]) => {
    const uniqueUrls: string[] = [];

    photoUrls.forEach((photoUrl) => {
      if (!photoUrl || uniqueUrls.includes(photoUrl)) {
        return;
      }

      uniqueUrls.push(photoUrl);
    });

    if (uniqueUrls.length === 0) {
      toast.error("No photos selected for deletion.");
      return;
    }

    setPhotosToDelete(uniqueUrls);
    setDeleteModalMessage(
      uniqueUrls.length === 1
        ? "Are you sure you want to delete this photo?"
        : `Are you sure you want to delete ${uniqueUrls.length} selected photos?`,
    );
    setDeleteModalOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setDeleteModalOpen(false);
    setPhotosToDelete([]);
    setDeleteModalMessage("");
  };

  const handleSetPrimaryPhoto = async (photo: any) => {
    const photoUrl = getPhotoUrl(photo);

    if (!photoUrl) {
      toast.error("Photo URL is missing.");
      return;
    }

    if (!userId) {
      toast.error("User ID is missing.");
      return;
    }

    LoaderService.show();

    try {
      const response = await PutUpdatePhotoStatus(userId, photoUrl, true);
      const isSuccess =
        response?.status === "success" ||
        response?.status === true ||
        response?.success === true ||
        response?.data?.status === true ||
        response?.data?.status === "success";

      if (isSuccess) {
        toast.success("Photo marked as primary.");
        window.location.reload();
      } else {
        toast.error(
          response?.message ||
            response?.data?.message ||
            "Failed to mark photo as primary.",
        );
      }
    } catch (error) {
      console.error("Failed to set primary photo", error);
      toast.error("Failed to mark photo as primary.");
    } finally {
      LoaderService.hide();
    }
  };

  const handleDeletePhotos = async () => {
    if (photosToDelete.length === 0) {
      toast.error("No photos selected for deletion.");
      return;
    }

    if (!userId) {
      toast.error("User ID is missing.");
      return;
    }

    LoaderService.show();

    try {
      const response = await DeleteUserPhotos(userId, photosToDelete);
      const isSuccess =
        response?.status === "success" ||
        response?.status === true ||
        response?.success === true ||
        response?.data?.status === true ||
        response?.data?.status === "success";

      if (isSuccess) {
        toast.success(
          photosToDelete.length === 1
            ? "Photo deleted successfully."
            : "Selected photos deleted successfully.",
        );
        setSelectedPhotoUrls([]);
        setIsSelectionMode(false);
        closeDeleteConfirmation();
        window.location.reload();
      } else {
        toast.error(
          response?.message ||
            response?.data?.message ||
            "Failed to delete selected photos.",
        );
      }
    } catch (error) {
      console.error("Failed to delete photo(s)", error);
      toast.error("Failed to delete selected photos.");
    } finally {
      LoaderService.hide();
    }
  };

  const fetchSupportAdmins = async () => {
    try {
      const response = await GetCustomerSupportAdmins();
      if (Array.isArray(response)) {
        setSupportAdmins(response);
      } else if (response && response.data) {
        setSupportAdmins(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch support admins", error);
    }
  };

  useEffect(() => {
    if (activeTab !== "chat" || !userId || whatsappChatData) {
      return;
    }

    const fetchWhatsappChat = async () => {
      setWhatsappChatLoading(true);
      try {
        const response: any = await GetUserWhatsappChat(userId);
        if (response?.status === "success") {
          shouldScrollWhatsappBottomRef.current = true;
          setWhatsappChatData(response.data);
        } else {
          toast.error(response?.message || "Failed to fetch WhatsApp chat.");
        }
      } catch (error) {
        console.error("Failed to fetch WhatsApp chat", error);
        toast.error("Failed to fetch WhatsApp chat.");
      } finally {
        setWhatsappChatLoading(false);
      }
    };

    fetchWhatsappChat();
  }, [activeTab, userId, whatsappChatData]);

  useEffect(() => {
    if (activeTab === "chat") {
      shouldScrollWhatsappBottomRef.current = true;
    }
  }, [activeTab]);

  useEffect(() => {
    if (!shouldScrollWhatsappBottomRef.current || whatsappOlderLoading) {
      return;
    }

    const scrollContainer = whatsappChatScrollRef.current;
    if (!scrollContainer) {
      return;
    }

    requestAnimationFrame(() => {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
      shouldScrollWhatsappBottomRef.current = false;
    });
  }, [activeTab, whatsappChatData?.messages?.length, whatsappOlderLoading]);

  const mergeWhatsappChatMessages = (currentMessages: any[], nextMessages: any[]) => {
    const seen = new Set<string>();
    return [...currentMessages, ...nextMessages].filter((message: any) => {
      const key = String(message.message_id || message.id || `${message.created_at}-${message.body}`);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fetchOlderWhatsappMessages = async () => {
    if (!userId || whatsappOlderLoading || whatsappChatLoading) return;

    const currentMessages = (whatsappChatData?.messages || []) as any[];
    const total = whatsappChatData?.total || 0;
    if (currentMessages.length >= total) return;

    setWhatsappOlderLoading(true);
    try {
      const response: any = await GetUserWhatsappChat(userId, 25, currentMessages.length);
      if (response?.status === "success") {
        const nextMessages = response.data?.messages || [];
        setWhatsappChatData((current: any) => ({
          ...(current || response.data),
          ...response.data,
          messages: mergeWhatsappChatMessages(current?.messages || currentMessages, nextMessages),
        }));
      } else {
        toast.error(response?.message || "Failed to fetch older messages.");
      }
    } catch (error) {
      console.error("Failed to fetch older WhatsApp messages", error);
      toast.error("Failed to fetch older messages.");
    } finally {
      setWhatsappOlderLoading(false);
    }
  };

  const handleWhatsappChatScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (event.currentTarget.scrollTop <= 80) {
      fetchOlderWhatsappMessages();
    }
  };

  const handleSendWhatsappChatMessage = async (message: string) => {
    if (!userId || whatsappMessageSending) {
      return;
    }

    setWhatsappMessageSending(true);
    try {
      const response: any = await PostUserWhatsappChatMessage(userId, {
        message,
        wa_id: whatsappChatData?.wa_id,
      });

      if (response?.status === "success" && response?.data) {
        shouldScrollWhatsappBottomRef.current = true;
        setWhatsappChatData((current: any) => ({
          ...(current || {}),
          wa_id: response.data.wa_id || current?.wa_id,
          messages: [response.data, ...((current?.messages || []) as any[])],
          total: (current?.total || 0) + 1,
          whatsapp_message_total: (current?.whatsapp_message_total || 0) + 1,
        }));
        toast.success("Message sent.");
      } else {
        toast.error(response?.detail?.message || response?.message || "Failed to send message.");
      }
    } catch (error) {
      console.error("Failed to send WhatsApp message", error);
      toast.error("Failed to send message.");
    } finally {
      setWhatsappMessageSending(false);
    }
  };

  const handleAssignUser = async (adminUsername: string) => {
    const userId = profile.UserId || profile.userId || profile.user_id;
    if (!userId) {
      toast.error("User ID not found");
      return;
    }
    try {
      LoaderService.show();
      const response = await PatchAssignUserInstruction(userId, adminUsername);
      if (response.status === true || response.success === true) {
        toast.success(response.message || "User assigned successfully");
        setIsAssigning(false);
        // Refresh to see updated assignment
        window.location.reload();
      } else {
        toast.error(response.message || "Assignment failed");
      }
    } catch (error) {
      console.error("Error assigning user", error);
      toast.error("An error occurred during assignment");
    } finally {
      LoaderService.hide();
    }
  };

  const fetchCasteData = async () => {
    try {
      const response = await GetCasteList();
      const list = response?.data || response;
      if (Array.isArray(list) && list.length > 0) {
        setCasteList(list);
      }
    } catch (err) {
      console.error("Failed to fetch caste list, using fallback:", err);
    }
  };

  const handleSetCommunity = async (community: string) => {
    const userId = profile.UserId || profile.userId || profile.user_id;
    if (!userId) {
      toast.error("User ID not found");
      return;
    }
    try {
      LoaderService.show();
      const response = await PostSetCommunity({ user_id: userId, community });
      if (response?.status === true) {
        toast.success(response.message || `Community set to ${community}`);
        setCasteDropdownOpen(false);
        setCasteSearchTerm("");
        window.location.reload();
      } else {
        toast.error(response?.message || "Failed to set community");
      }
    } catch (error) {
      console.error("Error setting community:", error);
      toast.error("Error setting community");
    } finally {
      LoaderService.hide();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error("File size exceeds 20MB");
      return;
    }

    const userId = profile.UserId || profile.userId || profile.user_id;
    if (!userId) {
      toast.error("User ID not found");
      return;
    }

    try {
      LoaderService.show();
      const response = await PostUploadBiodata(userId, file);
      if (response?.status === true) {
        toast.success(response.message || "Biodata uploaded successfully");
        window.location.reload();
      } else {
        toast.error(
          response?.message || response?.detail || "Failed to upload biodata",
        );
      }
    } catch (error) {
      console.error("Error uploading biodata:", error);
      toast.error("Error uploading biodata");
    } finally {
      LoaderService.hide();
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const normalizeEnumValue = (val: any): string => {
    if (!val) return "";
    return String(val).toUpperCase().replace(/\s+/g, "_");
  };

  const getEnumMappedValue = (field: string, value: any): any => {
    const valStr = normalizeEnumValue(value);
    const apiFieldName = field.toLowerCase();

    if (apiFieldName === "marital_status") {
      if (valStr.includes("UNMARRIED") || valStr.includes("NEVER"))
        return "UNMARRIED";
      if (valStr.includes("DIVORCED")) return "DIVORCED";
      if (valStr.includes("WIDOW")) return "WIDOW";
      if (valStr.includes("AWAITING")) return "AWAITING_DIVORCE";
      if (valStr.includes("NO_PREFERENCE") || valStr.includes("ANY"))
        return "NO_PREFERENCE";
    }
    if (apiFieldName === "family_background") {
      if (valStr.includes("RICH")) return "RICH";
      if (valStr.includes("UPPER_MIDDLE")) return "UPPER_MIDDLE_CLASS";
      if (
        valStr.includes("MIDDLE") &&
        !valStr.includes("UPPER") &&
        !valStr.includes("LOWER")
      )
        return "MIDDLE_CLASS";
      if (valStr.includes("LOWER_MIDDLE")) return "LOWER_MIDDLE_CLASS";
      if (valStr.includes("POOR")) return "POOR";
    }
    if (apiFieldName === "working_with") {
      if (valStr.includes("PRIVATE")) return "PRIVATE_SECTOR";
      if (valStr.includes("GOVERNMENT")) return "GOVERNMENT";
      if (valStr.includes("BUSINESS")) return "BUSINESS";
      if (valStr.includes("SELF")) return "SELF_EMPLOYED";
      if (valStr.includes("NOT_WORKING")) return "NOT_WORKING";
    }
    if (apiFieldName === "manglik") {
      if (valStr === "MANGLIK") return "Manglik";
      if (valStr.includes("NOT_MANGLIK")) return "Not_Manglik";
      if (valStr.includes("ANSHIK")) return "Anshik_Manglik";
      if (valStr.includes("PARTIALLY")) return "Partially";
      if (valStr.includes("NOT_SURE")) return "Not_Sure";
    }
    if (apiFieldName === "relocation_willingness") {
      if (valStr === "YES" || valStr === "TRUE") return "YES";
      if (valStr === "NO" || valStr === "FALSE") return "NO";
    }
    return value;
  };

  const mapValueForApi = (field: string, value: any): any => {
    const apiFieldName = field.toLowerCase();

    // Handle Age Range Object
    if (apiFieldName === "age" && Array.isArray(value) && value.length === 2) {
      return { min: Number(value[0]), max: Number(value[1]) };
    }

    // For multi-select fields like hobbies, ensure we send an array
    if (apiFieldName === "hobbies") {
      const arr =
        typeof value === "string"
          ? value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : value;
      const finalArr = Array.isArray(arr) ? arr : arr ? [arr] : [];
      return finalArr.map((v) => getEnumMappedValue(apiFieldName, v));
    }
    return getEnumMappedValue(apiFieldName, value);
  };

  const handlePreferenceEdit = async (
    field: string,
    value: any,
    isNegotiable: boolean,
  ) => {
    const userId = profile.UserId || profile.userId || profile.user_id;
    if (!userId) {
      toast.error("User ID not found");
      return;
    }

    // STRICT mapping - only these exact fields are allowed by the API
    const fieldMapping: Record<string, string> = {
      // Lifestyle
      Drinking: "drinking",
      drinking: "drinking",
      Smoking: "smoking",
      smoking: "smoking",
      DietaryHabits: "dietary_habits",
      Dietary: "dietary_habits",
      dietary_habits: "dietary_habits",

      // Personal
      Age: "age",
      age: "age",
      MinAge: "age",
      MaxAge: "age",
      AgeRange: "age", // Map age_range to age
      age_range: "age",

      MaritalStatus: "marital_status",
      marital_status: "marital_status",

      // Family
      FamilyType: "family_type",
      family_type: "family_type",
      FamilyBackground: "family_background",
      family_background: "family_background",

      // Professional
      Qualification: "qualification",
      qualification: "qualification",
      Profession: "profession",
      profession: "profession",
      WorkingWith: "working_with",
      working_with: "working_with",

      // Location
      Location: "location",
      location: "location",
      PreferredLocation: "location",
      preferred_location: "location",

      // Other
      Hobbies: "hobbies",
      hobbies: "hobbies",
      AnnualIncome: "annual_income",
      Income: "annual_income",
      annual_income: "annual_income",
      RelocationWillingness: "relocation_willingness",
      relocation_willingness: "relocation_willingness",
      Manglik: "manglik",
      manglik: "manglik",
    };

    // Get the API field name
    const apiFieldName = fieldMapping[field];

    // STRICT validation - reject if not in allowed list
    const allowedFields = [
      "drinking",
      "smoking",
      "dietary_habits",
      "age",
      "marital_status",
      "family_type",
      "family_background",
      "qualification",
      "location",
      "profession",
      "working_with",
      "hobbies",
      "annual_income",
      "relocation_willingness",
      "manglik",
    ];

    if (!apiFieldName || !allowedFields.includes(apiFieldName)) {
      console.error(`[Preference Edit] REJECTED - Unknown field: "${field}"`);
      toast.error(`Cannot edit "${field}" - field not supported`);
      setOpenEditDropdown(null);
      return;
    }

    console.log(
      `[Preference Edit] ✓ VALID - Field: "${field}" -> API: "${apiFieldName}"`,
    );

    try {
      LoaderService.show();

      // Value Normalization based on requested ENUMS and multi-select formats
      const apiValue = mapValueForApi(apiFieldName, value);

      const response = await UpdatePreferenceNegotiable(
        userId,
        apiFieldName,
        apiValue,
        isNegotiable,
      );

      if (response.status === "success") {
        toast.success(
          `Updated ${field} to ${isNegotiable ? "negotiable" : "non-negotiable"}`,
        );
        setOpenEditDropdown(null);
        window.location.reload();
      } else {
        toast.error(response.message || "Failed to update preference");
      }
    } catch (error) {
      console.error("Error updating preference:", error);
      toast.error("Error updating preference");
    } finally {
      LoaderService.hide();
    }
  };

  const handleValueUpdate = async (
    field: string,
    newValue: any,
    isNegotiable: boolean,
  ) => {
    const userId = profile.UserId || profile.userId || profile.user_id;
    if (!userId) {
      toast.error("User ID not found");
      return;
    }

    try {
      LoaderService.show();
      // Use the mapping logic similar to handlePreferenceEdit to get the correct API field name
      const fieldMapping: Record<string, string> = {
        Drinking: "drinking",
        Smoking: "smoking",
        DietaryHabits: "dietary_habits",
        Dietary: "dietary_habits",
        Age: "age",
        AgeRange: "age",
        MaritalStatus: "marital_status",
        FamilyType: "family_type",
        FamilyBackground: "family_background",
        Qualification: "qualification",
        Profession: "profession",
        WorkingWith: "working_with",
        Location: "location",
        PreferredLocation: "location",
        Hobbies: "hobbies",
        AnnualIncome: "annual_income",
        Income: "annual_income",
        RelocationWillingness: "relocation_willingness",
        Manglik: "manglik",
        IsManglik: "manglik",
      };
      const apiFieldName =
        fieldMapping[field] ||
        field
          .replace(/([A-Z])/g, "_$1")
          .toLowerCase()
          .replace(/^_/, "");

      // Value Normalization based on requested ENUMS and multi-select formats
      const apiValue = mapValueForApi(apiFieldName, newValue);

      const resp: any = await UpdatePreferenceNegotiable(
        userId,
        apiFieldName,
        apiValue,
        isNegotiable,
      );
      if (
        resp.status === "success" ||
        resp.success === true ||
        resp.status === "true"
      ) {
        toast.success(`Updated ${field} successfully`);
        setOpenEditDropdown(null);
        window.location.reload();
      } else {
        toast.error(resp.message || "Failed to update preference");
      }
    } catch (err) {
      console.error("Error updating preference value:", err);
      toast.error("Error updating preference");
    } finally {
      LoaderService.hide();
    }
  };

  const getDropdownOptions = (title: string) => {
    if (!preferenceCategories) return null;
    // Normalize title: "DietaryHabits" -> "Dietary Habits"
    const normalizedSearchTitle = title
      .replace(/([A-Z])/g, " $1")
      .trim()
      .toLowerCase();

    for (const category of preferenceCategories) {
      if (!category.preferences) continue;
      const pref = category.preferences.find(
        (p: any) =>
          p.title.toLowerCase() === normalizedSearchTitle ||
          p.title.replace(/\s+/g, "").toLowerCase() === title.toLowerCase(),
      );
      if (pref) return pref;
    }
    return null;
  };

  /* Helper to get normalized value from data object supporting different casings */
  const getValue = (data: any, key: string) => {
    if (!data) return undefined;

    // 1. Try exact match
    if (data[key] !== undefined) return data[key];

    // 2. Try snake_case (assuming key is PascalCase or camelCase)
    const snakeKey = key
      .replace(/([A-Z])/g, "_$1")
      .toLowerCase()
      .replace(/^_/, "");
    if (data[snakeKey] !== undefined) return data[snakeKey];

    // 3. Try lowercase
    const lowerKey = key.toLowerCase();
    if (data[lowerKey] !== undefined) return data[lowerKey];

    // 4. Broad case-insensitive search (Safety net for things like WeightKG vs WeightKg)
    const insensitiveKey = Object.keys(data).find(
      (k) => k.toLowerCase() === key.toLowerCase(),
    );
    if (insensitiveKey) return data[insensitiveKey];

    return undefined;
  };

  const renderDataGrid = (keys: string[], data: any, isIncome = false) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {keys.map((key) => {
          let val = getValue(data, key);
          if (val === undefined) return null;

          // Added for MaternalGotra to show "None" if null or empty
          if ((val === null || val === "") && key === "MaternalGotra") {
            val = "None";
          }

          if (val === null || val === "") return null;

          let displayVal = Array.isArray(val) ? val.join(", ") : val.toString();
          if (
            (key.toLowerCase().includes("income") || isIncome) &&
            !isNaN(Number(val))
          ) {
            displayVal = `₹${Number(val).toLocaleString()}`;
          } else if (
            typeof val === "string" &&
            val.length > 0 &&
            !val.includes("@") &&
            !val.includes("://")
          ) {
            // Capitalize words for common profile fields (like "arts & humanities" -> "Arts & Humanities")
            displayVal = val
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");
          }
          // Added for Intercaste
          if (typeof val === "boolean") {
            displayVal = val ? "Yes" : "No";
          }

          const isYesNo =
            val === "Yes" || val === "No" || typeof val === "boolean";
          const colorClass = isYesNo
            ? val === "Yes" || val === true
              ? "text-emerald-400"
              : "text-red-400"
            : "text-slate-100";

          return (
            <div
              key={key}
              className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/50 hover:bg-slate-800/60 transition-colors"
            >
              <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest mb-1">
                {key.replace(/([A-Z])/g, " $1").trim()}
              </div>
              <div className={`text-sm font-semibold ${colorClass}`}>
                {displayVal}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPreferenceItem = (key: string, value: any) => {
    // Allow 0 as valid value (e.g. income)
    if (
      value === undefined ||
      value === null ||
      key === "IsActive" ||
      key === "CreatedAt" ||
      key === "UpdatedAt"
    )
      return null;

    let displayValue = "";
    let isNegotiable: boolean | undefined = undefined;

    if (typeof value === "object") {
      if ("value" in value) {
        displayValue = value.value;
        isNegotiable = value.negotiable;
      } else if ("name" in value) {
        displayValue = value.name;
        isNegotiable = value.negotiable;
      } else if ("min" in value && "max" in value) {
        displayValue = `${value.min} - ${value.max}`;
        isNegotiable = value.negotiable;
      } else if ("preferred_location" in value) {
        displayValue = value.preferred_location;
        isNegotiable = value.negotiable;
      } else {
        // Avoid raw JSON unless absolutely necessary
        return null;
      }
    } else {
      displayValue = value.toString();
    }

    // Handle empty string or effectively empty values
    if (
      displayValue === "" ||
      displayValue === null ||
      displayValue === undefined
    ) {
      if (isNegotiable === true) {
        displayValue = "Any";
      } else {
        displayValue = "-";
      }
    }

    const isDropdownOpen = openEditDropdown === key;

    return (
      <div
        key={key}
        className="bg-slate-800/40 p-4 rounded-lg border border-slate-700/50 flex justify-between items-center group hover:bg-slate-800/60 transition-all relative"
      >
        <div>
          <div className="text-[10px] text-slate-500 uppercase font-black mb-1">
            {key
              .replace(/([A-Z])/g, " $1")
              .replace(/_/g, " ")
              .trim()}
          </div>
          <div className="text-sm font-bold text-slate-200">{displayValue}</div>
        </div>
        <div className="flex items-center gap-2">
          {isNegotiable !== undefined && (
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isNegotiable ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-slate-700/50 text-slate-400 border border-slate-600/20"}`}
            >
              {isNegotiable ? "Negotiable" : "Non-Negotiable"}
            </span>
          )}

          {/* Edit Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const isOpen = openEditDropdown === key;
              setOpenEditDropdown(isOpen ? null : key);

              if (!isOpen) {
                const isMultiSelect = key.toLowerCase() === "hobbies";
                const isAgeRange = key.toLowerCase().includes("age");

                let initial: any[] = [];

                if (
                  isAgeRange &&
                  typeof value === "object" &&
                  value !== null &&
                  "min" in value
                ) {
                  initial = [value.min, value.max];
                } else {
                  let raw =
                    typeof value === "object" && value !== null
                      ? value.value || value.name || value.preferred_location
                      : value;

                  if (isMultiSelect && typeof raw === "string") {
                    raw = raw
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean);
                  }
                  initial = Array.isArray(raw) ? [...raw] : raw ? [raw] : [];
                }

                setPendingSelections((prev) => ({ ...prev, [key]: initial }));
              }
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded text-[10px] font-bold uppercase"
          >
            Edit
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-full right-0 mt-2 z-50 bg-[#0f172a] border border-blue-500/40 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5),0_0_20px_rgba(59,130,246,0.2)] overflow-hidden animate-slide-down min-w-[280px] backdrop-blur-xl">
              {/* Status Section (Negotiable) - NOW ON TOP */}
              <div className="p-1.5 bg-white/[0.03] border-b border-white/5">
                <div className="px-3 py-1.5 text-[8px] font-black text-slate-500 uppercase tracking-widest">
                  Match Policy
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreferenceEdit(key, displayValue, true);
                    }}
                    className={`flex-1 px-3 py-2 text-center text-[10px] font-bold transition-all rounded-lg flex items-center justify-center gap-2 ${isNegotiable === true ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "text-slate-500 hover:bg-white/5 hover:text-slate-300 border border-transparent"}`}
                  >
                    <span>✅</span> Negotiable
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreferenceEdit(key, displayValue, false);
                    }}
                    className={`flex-1 px-3 py-2 text-center text-[10px] font-bold transition-all rounded-lg flex items-center justify-center gap-2 ${isNegotiable === false ? "bg-red-500/10 text-red-400 border border-red-500/20" : "text-slate-500 hover:bg-white/5 hover:text-slate-300 border border-transparent"}`}
                  >
                    <span>⛔</span> Non-Negotiable
                  </button>
                </div>
              </div>

              {/* Options Section */}
              {(() => {
                const fieldMapping: Record<string, string> = {
                  Drinking: "drinking",
                  Smoking: "smoking",
                  DietaryHabits: "dietary_habits",
                  Dietary: "dietary_habits",
                  Age: "age",
                  AgeRange: "age",
                  MaritalStatus: "marital_status",
                  FamilyType: "family_type",
                  FamilyBackground: "family_background",
                  Qualification: "qualification",
                  Profession: "profession",
                  WorkingWith: "working_with",
                  Location: "location",
                  PreferredLocation: "location",
                  Hobbies: "hobbies",
                  AnnualIncome: "annual_income",
                  Income: "annual_income",
                  RelocationWillingness: "relocation_willingness",
                  Manglik: "manglik",
                  IsManglik: "manglik",
                };
                const apiFieldName =
                  fieldMapping[key] ||
                  key
                    .replace(/([A-Z])/g, "_$1")
                    .toLowerCase()
                    .replace(/^_/, "");
                const prefOptions = getDropdownOptions(key);
                const isAgeField = apiFieldName === "age";

                if (isAgeField) {
                  const currentRange = pendingSelections[key] || [19, 60];
                  const [minAge, maxAge] = currentRange;

                  return (
                    <div className="p-4 bg-slate-900/40 space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          Age Range
                        </span>
                        <span className="text-xs font-bold text-blue-400">
                          {minAge} - {maxAge} years
                        </span>
                      </div>

                      <div className="space-y-6 pt-2 pb-4">
                        <div className="relative h-1.5 bg-slate-700 rounded-full">
                          <div
                            className="absolute h-full bg-blue-500 rounded-full"
                            style={{
                              left: `${((minAge - 18) / (70 - 18)) * 100}%`,
                              right: `${100 - ((maxAge - 18) / (70 - 18)) * 100}%`,
                            }}
                          />
                          <input
                            type="range"
                            min="18"
                            max="70"
                            value={minAge}
                            onChange={(e) => {
                              const val = Math.min(
                                Number(e.target.value),
                                maxAge - 1,
                              );
                              setPendingSelections((prev) => ({
                                ...prev,
                                [key]: [val, maxAge],
                              }));
                            }}
                            className="absolute w-full h-1.5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg"
                          />
                          <input
                            type="range"
                            min="18"
                            max="70"
                            value={maxAge}
                            onChange={(e) => {
                              const val = Math.max(
                                Number(e.target.value),
                                minAge + 1,
                              );
                              setPendingSelections((prev) => ({
                                ...prev,
                                [key]: [minAge, val],
                              }));
                            }}
                            className="absolute w-full h-1.5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg"
                          />
                        </div>

                        <div className="flex justify-between text-[10px] text-slate-500 font-bold">
                          <span>18</span>
                          <span>70</span>
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleValueUpdate(
                            key,
                            [minAge, maxAge],
                            !!isNegotiable,
                          );
                        }}
                        className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black uppercase rounded-lg transition-all shadow-lg active:scale-95"
                      >
                        Update Age Range
                      </button>
                    </div>
                  );
                }

                if (
                  prefOptions &&
                  (prefOptions.type === "options" ||
                    prefOptions.type === "checkbox")
                ) {
                  return (
                    <div className="max-h-[320px] overflow-hidden flex flex-col">
                      <div className="px-4 py-2 text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] bg-white/[0.02] border-b border-white/5">
                        Value Options
                      </div>

                      <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5 custom-scrollbar min-h-0">
                        {prefOptions.values.map((opt: any) => {
                          const isMultiSelect =
                            prefOptions.type === "checkbox" ||
                            key.toLowerCase() === "hobbies";
                          const currentPending = pendingSelections[key] || [];
                          const normalizedOpt = getEnumMappedValue(
                            apiFieldName,
                            opt.value,
                          );

                          const isSelected = isMultiSelect
                            ? currentPending.some(
                                (v) =>
                                  getEnumMappedValue(apiFieldName, v) ===
                                  normalizedOpt,
                              )
                            : getEnumMappedValue(apiFieldName, displayValue) ===
                              normalizedOpt;

                          return (
                            <button
                              key={opt.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isMultiSelect) {
                                  let newArray = [...currentPending];
                                  const valIndex = newArray.findIndex(
                                    (v) =>
                                      getEnumMappedValue(apiFieldName, v) ===
                                      normalizedOpt,
                                  );
                                  if (valIndex > -1) {
                                    newArray.splice(valIndex, 1);
                                  } else {
                                    if (newArray.length >= 5) {
                                      toast.error(
                                        "You can select at most 5 options",
                                      );
                                      return;
                                    }
                                    newArray.push(opt.value);
                                  }
                                  setPendingSelections((prev) => ({
                                    ...prev,
                                    [key]: newArray,
                                  }));
                                } else {
                                  handleValueUpdate(
                                    key,
                                    opt.value,
                                    !!isNegotiable,
                                  );
                                }
                              }}
                              className={`w-full px-3 py-2 text-left text-[11px] font-bold transition-all rounded-lg flex items-center justify-between group/opt ${isSelected ? "bg-blue-600/20 text-blue-400" : "text-slate-400 hover:bg-white/5 hover:text-slate-200"}`}
                            >
                              <div className="flex items-center gap-2">
                                {isMultiSelect && (
                                  <div
                                    className={`w-3.5 h-3.5 rounded border transition-colors flex items-center justify-center ${isSelected ? "bg-blue-600 border-blue-500" : "border-slate-600 group-hover/opt:border-slate-500"}`}
                                  >
                                    {isSelected && (
                                      <svg
                                        className="w-2.5 h-2.5 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={3}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                )}
                                <span>{opt.value}</span>
                              </div>
                              {isSelected && !isMultiSelect && (
                                <span className="text-[10px]">●</span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {(prefOptions.type === "checkbox" ||
                        key.toLowerCase() === "hobbies") && (
                        <div className="p-2 border-t border-white/5 bg-white/[0.04]">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleValueUpdate(
                                key,
                                pendingSelections[key] || [],
                                !!isNegotiable,
                              );
                            }}
                            className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black uppercase rounded-lg transition-all shadow-lg shadow-blue-600/20 active:scale-95"
                          >
                            Save & Update
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Internal Tabs */}
      <div className="flex gap-1 bg-slate-900/50 p-1 rounded-xl border border-slate-800 w-fit">
        {[
          { id: "profile", label: "Profile Details", icon: "👤" },
          { id: "preference", label: "Preferences", icon: "⚙️" },
          { id: "photos", label: "Gallery", icon: "📸" },
          { id: "biodata", label: "Bio Data", icon: "📄" },
          { id: "chat", label: "Chat", icon: "" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
              activeTab === tab.id
                ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                : tab.id === "chat"
                  ? "text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10 hover:text-emerald-300"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}
          >
            {tab.id === "chat" ? (
              <MessageCircle size={14} />
            ) : (
              <span>{tab.icon}</span>
            )}
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {activeTab === "profile" && (
          <div className="space-y-6">
            {/* Assignment Section (Highest priority) */}
            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl overflow-hidden shadow-lg shadow-indigo-500/5">
              <div className="p-4 bg-indigo-500/10 border-b border-indigo-500/20 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
                    <span className="text-xl">👩‍💻</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-black text-indigo-400 text-[10px] uppercase tracking-widest">
                      Profile Manager
                    </span>
                    <span className="text-[9px] text-indigo-500/70 font-bold uppercase">
                      Customer Support Assignment
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="px-4 py-1.5 bg-indigo-500/20 rounded-full border border-indigo-500/30">
                    <span className="text-xs font-black text-indigo-200 uppercase tracking-wider">
                      Assigned To:{" "}
                      {getValue(profile, "AssignedTo") ||
                        getValue(profile, "assigned_to") ||
                        "UNASSIGNED"}
                    </span>
                  </div>

                  {/* Assignment Logic */}
                  <div className="relative">
                    {isAssigning ? (
                      <div className="flex items-center gap-1 bg-slate-950 border border-indigo-500/40 rounded-lg p-1 animate-in slide-in-from-right-2 duration-200">
                        <select
                          className="bg-transparent text-[10px] font-black text-indigo-400 focus:outline-none cursor-pointer uppercase pr-6 py-1 pl-2"
                          onChange={(e) => handleAssignUser(e.target.value)}
                          defaultValue=""
                        >
                          <option value="" disabled className="bg-slate-900">
                            Select Admin
                          </option>
                          {supportAdmins.map((admin: any) => (
                            <option
                              key={admin.id}
                              value={admin.username}
                              className="bg-slate-900"
                            >
                              {admin.username}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => setIsAssigning(false)}
                          className="p-1 hover:text-rose-500 transition-colors text-slate-500"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setIsAssigning(true);
                          if (supportAdmins.length === 0) fetchSupportAdmins();
                        }}
                        className="p-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 rounded-lg transition-all flex items-center gap-1.5 group/edit"
                        title="Change Assignment"
                      >
                        <Edit2
                          size={12}
                          className="group-hover/edit:scale-110 transition-transform"
                        />
                        <span className="text-[10px] font-black uppercase tracking-widest">
                          Edit
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Instructions/Notes Section (Highest priority if present) */}
            {getValue(profile, "adminNote") && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl overflow-hidden shadow-lg shadow-amber-500/5">
                <div className="p-4 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-3">
                  <span className="text-xl">📋</span>
                  <div className="flex flex-col">
                    <span className="font-black text-amber-400 text-[10px] uppercase tracking-widest">
                      Special Instructions
                    </span>
                    <span className="text-[9px] text-amber-500/70 font-bold uppercase">
                      Critical Matchmaking Context
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-slate-900/40">
                  <p className="text-amber-200/90 text-sm leading-relaxed font-medium italic">
                    "{getValue(profile, "adminNote")}"
                  </p>
                </div>
              </div>
            )}

            {/* Personal Section */}
            <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
              <div className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors">
                <button
                  onClick={() => toggleSection("personal")}
                  className="flex items-center gap-3"
                >
                  <span className="text-xl">👤</span>
                  <span className="font-bold text-slate-200">
                    Personal Information
                  </span>
                  {expandedSections.personal ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const userId =
                      profile.UserId || profile.userId || profile.user_id;
                    if (userId) {
                      navigate(`/users/edit/${userId}`);
                    } else {
                      toast.error("User ID not found");
                    }
                  }}
                  className="p-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded-lg transition-all flex items-center gap-1.5 group/edit"
                  title="Edit Profile"
                >
                  <Edit2
                    size={12}
                    className="group-hover/edit:scale-110 transition-transform"
                  />
                  <span className="text-[10px] font-black uppercase tracking-widest">
                    Edit All
                  </span>
                </button>
              </div>
              {expandedSections.personal && (
                <div className="p-4 bg-slate-900/20">
                  {/* Added Intercaste and MaternalGotra*/}
                  {renderDataGrid(
                    [
                      "Name",
                      "Gender",
                      "Age",
                      "DateOfBirth",
                      "BirthTime",
                      "HeightCm",
                      "WeightKg",
                      "Complexion",
                      "MaritalStatus",
                      "BloodGroup",
                      "DisabilityStatus",
                      "ManagedBy",
                      "IsAstrologyImp",
                      "has_child",
                      "Intercaste",
                      "MaternalGotra",
                    ],
                    profile,
                  )}
                </div>
              )}
            </div>

            {/* About Section */}
            {getValue(profile, "AboutMe") && (
              <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection("about")}
                  className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📝</span>
                    <span className="font-bold text-slate-200">About Me</span>
                  </div>
                  {expandedSections.about ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                </button>
                {expandedSections.about && (
                  <div className="p-4 bg-slate-900/20">
                    <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                      {getValue(profile, "AboutMe")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Professional Section */}
            <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("professional")}
                className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🎓</span>
                  <span className="font-bold text-slate-200">
                    Professional & Education
                  </span>
                </div>
                {expandedSections.professional ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronRight size={18} />
                )}
              </button>
              {expandedSections.professional && (
                <div className="p-4 bg-slate-900/20">
                  {renderDataGrid(
                    [
                      "Occupation",
                      "Qualification",
                      "AdditionalQualification",
                      "FieldOfStudy",
                      "CollegeName",
                      "WorkingWith",
                      "OccupationCompany",
                      "OccupationLocation",
                      "AnnualIncomeMin",
                      "AnnualIncomeMax",
                      "MinAnnualIncome",
                      "MaxAnnualIncome",
                    ],
                    profile,
                  )}
                </div>
              )}
            </div>

            {/* Family Section */}
            <div
              className={`bg-slate-900/30 border border-slate-800/50 rounded-xl ${casteDropdownOpen ? "overflow-visible" : "overflow-hidden"}`}
            >
              <button
                onClick={() => toggleSection("family")}
                className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">👨‍👩‍👧‍👦</span>
                  <span className="font-bold text-slate-200">
                    Family & Background
                  </span>
                </div>
                {expandedSections.family ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronRight size={18} />
                )}
              </button>
              {expandedSections.family && (
                <div className="p-4 bg-slate-900/20 space-y-4">
                  {renderDataGrid(
                    [
                      "FamilyType",
                      "FamilyBackground",
                      "FamilyOccupation",
                      "FatherName",
                      "MotherName",
                      "MotherOccupation",
                      "FamilyIncomeMin",
                      "FamilyIncomeMax",
                      "minFamilyIncome",
                      "maxFamilyIncome",
                      "SiblingCount",
                      "SubCaste",
                      "Gotra",
                      "AboutFamily",
                    ],
                    profile,
                  )}

                  {/* Editable Caste / Community Field */}
                  <div className="relative">
                    <div className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/50 hover:bg-slate-800/60 transition-colors group">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest mb-1">
                            Caste / Community
                          </div>
                          <div className="text-sm font-semibold text-slate-100">
                            {(() => {
                              const val =
                                getValue(profile, "Caste") ||
                                getValue(profile, "community");
                              if (!val || val === "N/A")
                                return (
                                  <span className="text-slate-500 italic">
                                    Not Set
                                  </span>
                                );
                              return val;
                            })()}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const nextState = !casteDropdownOpen;
                            setCasteDropdownOpen(nextState);
                            if (nextState) {
                              fetchCasteData();
                            }
                            setCasteSearchTerm("");
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded-lg text-[10px] font-bold uppercase tracking-wider"
                        >
                          {casteDropdownOpen ? "Close" : "Edit"}
                        </button>
                      </div>
                    </div>

                    {/* Caste Dropdown */}
                    {casteDropdownOpen && (
                      <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-[#0f172a] border border-blue-500/40 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5),0_0_20px_rgba(59,130,246,0.2)] overflow-hidden animate-slide-down backdrop-blur-xl">
                        {/* Search */}
                        <div className="p-3 border-b border-white/5 bg-white/[0.03]">
                          <div className="relative">
                            <Search
                              size={14}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                            />
                            <input
                              type="text"
                              value={casteSearchTerm}
                              onChange={(e) =>
                                setCasteSearchTerm(e.target.value)
                              }
                              placeholder="Search caste..."
                              className="w-full pl-9 pr-3 py-2 bg-slate-900/60 border border-slate-700/50 rounded-lg text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                              autoFocus
                            />
                          </div>
                        </div>

                        {/* Options */}
                        <div className="max-h-[400px] overflow-y-auto p-1.5 space-y-0.5 custom-scrollbar">
                          {casteList
                            .filter((c) =>
                              c
                                .toLowerCase()
                                .includes(casteSearchTerm.toLowerCase()),
                            )
                            .map((caste) => {
                              const currentCaste =
                                getValue(profile, "Caste") ||
                                getValue(profile, "community") ||
                                "";
                              const isSelected =
                                currentCaste.toLowerCase() ===
                                caste.toLowerCase();
                              return (
                                <button
                                  key={caste}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetCommunity(caste);
                                  }}
                                  className={`w-full px-3 py-2 text-left text-[11px] font-bold transition-all rounded-lg flex items-center justify-between ${
                                    isSelected
                                      ? "bg-blue-600/20 text-blue-400"
                                      : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                                  }`}
                                >
                                  <span>{caste}</span>
                                  {isSelected && (
                                    <span className="text-[10px]">●</span>
                                  )}
                                </button>
                              );
                            })}
                          {casteList.filter((c) =>
                            c
                              .toLowerCase()
                              .includes(casteSearchTerm.toLowerCase()),
                          ).length === 0 && (
                            <div className="px-3 py-4 text-center text-slate-500 text-xs italic">
                              No matching caste found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Location Section */}
            <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("contact")}
                className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">📍</span>
                  <span className="font-bold text-slate-200">
                    Contact & Location
                  </span>
                </div>
                {expandedSections.contact ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronRight size={18} />
                )}
              </button>
              {expandedSections.contact && (
                <div className="p-4 bg-slate-900/20">
                  {renderDataGrid(
                    [
                      "Email",
                      "Phone",
                      "MatrimonialPhone",
                      "UserPhone",
                      "AlternateContact",
                      "Address",
                      "City",
                      "State",
                      "Country",
                      "CountryCode",
                      "HomeTown",
                      "BirthPlace",
                      "NativePlace",
                      "LocationType",
                      "Latitude",
                      "Longitude",
                      "BirthLatitude",
                      "BirthLongitude",
                    ],
                    profile,
                  )}
                </div>
              )}
            </div>

            {/* Lifestyle Section */}
            <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("lifestyle")}
                className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🌟</span>
                  <span className="font-bold text-slate-200">
                    Lifestyle & Extras
                  </span>
                </div>
                {expandedSections.lifestyle ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronRight size={18} />
                )}
              </button>
              {expandedSections.lifestyle && (
                <div className="p-4 bg-slate-900/20">
                  {renderDataGrid(
                    [
                      "Dietary",
                      "Drinking",
                      "Smoking",
                      "Hobbies",
                      "LanguagesKnown",
                      "Manglik",
                      "IsManglik",
                      "IsAstrologyImp",
                      "IsGunnMatchingImportant",
                      "IsQuestionnaireComplete",
                      "TagsAllocated",
                    ],
                    profile,
                  )}
                </div>
              )}
            </div>
            {/* Children Section */}
            {(() => {
              const hasChildVal = getValue(profile, "has_child");
              const children = getValue(profile, "children") || [];
              const isHasChild =
                hasChildVal === true ||
                String(hasChildVal).toLowerCase() === "true" ||
                hasChildVal === 1 ||
                hasChildVal === "1" ||
                hasChildVal === "Yes" ||
                (Array.isArray(children) && children.length > 0);

              if (!isHasChild) return null;

              return (
                <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection("children")}
                    className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">👶</span>
                      <span className="font-bold text-slate-200">
                        Children Details
                      </span>
                    </div>
                    {expandedSections.children ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronRight size={18} />
                    )}
                  </button>
                  {expandedSections.children && (
                    <div className="p-4 bg-slate-900/20">
                      {/* Custom rendering for children array */}
                      {(() => {
                        if (!Array.isArray(children) || children.length === 0)
                          return (
                            <div className="text-slate-500 text-sm">
                              No children details provided.
                            </div>
                          );

                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {children.map((child: any, index: number) => (
                              <div
                                key={index}
                                className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/50"
                              >
                                <div className="text-[10px] text-slate-500 uppercase font-black tracking-widest mb-2 border-b border-slate-700 pb-1">
                                  Child {index + 1}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <div className="text-[9px] text-slate-500">
                                      Name
                                    </div>
                                    <div className="text-sm font-semibold text-slate-200">
                                      {child.name || child.Name || "N/A"}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[9px] text-slate-500">
                                      Age
                                    </div>
                                    <div className="text-sm font-semibold text-slate-200">
                                      {child.age || child.Age || "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === "preference" && (
          <div className="space-y-6">
            {[
              {
                title: "Personal Preferences",
                icon: "💑",
                keys: [
                  "Age",
                  "AgeRange",
                  "MaritalStatus",
                  "FamilyType",
                  "FamilyBackground",
                  "ChildrenStatus",
                ],
              },
              {
                title: "Lifestyle Expectations",
                icon: "🌟",
                // Removed "Lifestyle" to prevent raw JSON object rendering since children are already covered
                keys: ["Dietary", "DietaryHabits", "Drinking", "Smoking"],
              },
              {
                title: "Education & Career",
                icon: "🎓",
                keys: [
                  "Qualification",
                  "Location",
                  "Profession",
                  "Occupation",
                  "WorkingWith",
                  "Hobbies",
                  "Citizenship",
                ],
              },
              {
                title: "Financial & Other",
                icon: "💰",
                keys: [
                  "AnnualIncome",
                  "Income",
                  "RelocationWillingness",
                  "ResidencyStatus",
                ],
              },
            ].map((section) => {
              // Find all available keys for this section from the preference object
              const sectionData = section.keys
                .map((key) => {
                  // Start by looking for the key directly (normalized)
                  // We support snake_case in data vs PascalCase in our config
                  // e.g. Config 'DietaryHabits' -> Data 'dietary_habits'
                  const normalizedKey = key
                    .replace(/([A-Z])/g, "_$1")
                    .toLowerCase()
                    .replace(/^_/, "");

                  let val =
                    getValue(preference, key) ||
                    getValue(preference, normalizedKey);

                  // If not found in root, specifically check the 'lifestyle' nested object for lifestyle keys
                  // and 'personal'/'additional' etc if they exist.
                  if (val === undefined) {
                    const categories = [
                      "lifestyle",
                      "personal",
                      "additional",
                      "preferences",
                    ];
                    for (const cat of categories) {
                      if (preference[cat]) {
                        // Try finding the key inside this category
                        const innerVal =
                          getValue(preference[cat], key) ||
                          getValue(preference[cat], normalizedKey);
                        if (innerVal !== undefined) {
                          val = innerVal;
                          break;
                        }
                      }
                    }
                  }

                  return { key, value: val };
                })
                .filter(
                  (item) =>
                    item.value !== undefined &&
                    item.value !== null &&
                    item.value !== "",
                );

              if (sectionData.length === 0) return null;

              return (
                <div
                  key={section.title}
                  className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-4"
                >
                  <h3 className="text-blue-400 font-bold mb-4 flex items-center gap-2 uppercase text-xs tracking-wider">
                    <span>{section.icon}</span> {section.title}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {sectionData.map(({ key, value }) =>
                      renderPreferenceItem(key, value),
                    )}
                  </div>
                </div>
              );
            })}

            {/* Catch-all for any unknown keys that were NOT rendered in the sections above */}
            {Object.entries(preference).map(([key, value]) => {
              // Keys we have explicitly handled in the groups above
              const handledKeys = [
                "age",
                "age_range",
                "marital_status",
                "family_type",
                "family_background",
                "manglik",
                "children_status",
                "dietary",
                "dietary_habits",
                "drinking",
                "smoking",
                "lifestyle",
                "qualification",
                "location",
                "profession",
                "occupation",
                "working_with",
                "hobbies",
                "citizenship",
                "annual_income",
                "income",
                "relocation_willingness",
                "residency_status",
                "is_active",
                "created_at",
                "updated_at",
              ];

              const normalizedKey = key.toLowerCase();
              // If this key (or its snake_case version) is in our handled list, don't double render
              if (handledKeys.includes(normalizedKey)) return null;

              // If it is a container object like 'lifestyle' or 'personal' which we have already mined for data, skip it
              if (
                ["lifestyle", "personal", "additional", "preferences"].includes(
                  normalizedKey,
                )
              )
                return null;

              // Render simple leftover keys
              return (
                <div
                  key={key}
                  className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-4"
                >
                  <h4 className="text-slate-400 font-bold mb-4 uppercase text-xs border-b border-slate-700 pb-2">
                    {key.replace(/_/g, " ")}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderPreferenceItem(key, value)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === "photos" && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-bold text-slate-100">Gallery</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {galleryPhotos.length} photo{galleryPhotos.length === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {galleryPhotos.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsSelectionMode((current) => !current);
                      setSelectedPhotoUrls([]);
                    }}
                    className="px-3 py-2 rounded-lg border border-slate-700 bg-slate-900/80 text-[10px] font-black uppercase tracking-[0.18em] text-slate-200 transition hover:border-rose-500/60 hover:text-rose-200"
                  >
                    {isSelectionMode ? "Cancel" : "Delete More"}
                  </button>
                )}
                {isSelectionMode && (
                  <button
                    type="button"
                    disabled={selectedPhotoUrls.length === 0}
                    onClick={() => openDeleteConfirmation(selectedPhotoUrls)}
                    className="px-3 py-2 rounded-lg bg-rose-600 text-[10px] font-black uppercase tracking-[0.18em] text-white transition disabled:opacity-40 disabled:cursor-not-allowed hover:bg-rose-500"
                  >
                    Delete {selectedPhotoUrls.length} Selected
                  </button>
                )}
              </div>
            </div>

            {isSelectionMode && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[11px] text-amber-100">
                Select one or more photos to delete them in one go. Use the delete button on any photo for a single removal.
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryPhotos.length > 0 ? (
                galleryPhotos.map((photo, idx) => {
                  const photoUrl = getPhotoUrl(photo);

                  if (!photoUrl) {
                    return null;
                  }

                  const isSelected = selectedPhotoUrls.includes(photoUrl);
                  const isPrimary = isPrimaryPhoto(photo);

                  return (
                    <div
                      key={`${photoUrl}-${idx}`}
                      className={`relative group aspect-[3/4] rounded-xl overflow-hidden border bg-slate-900 cursor-pointer ${
                        isSelected
                          ? "border-rose-500/70"
                          : "border-slate-800"
                      }`}
                      onClick={() => {
                        if (isSelectionMode) {
                          togglePhotoSelection(photoUrl);
                          return;
                        }

                        setSelectedImage(photoUrl);
                      }}
                    >
                      <img
                        src={photoUrl}
                        alt={`Gallery ${idx}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      {isPrimary && (
                        <div className="absolute top-2 right-2 bg-blue-600 text-white text-[10px] font-black px-2 py-1 rounded-md shadow-lg">
                          PRIMARY
                        </div>
                      )}

                      {isSelectionMode && (
                        <div className="absolute top-2 left-2">
                          <div
                            className={`flex h-6 w-6 items-center justify-center rounded-full border ${
                              isSelected
                                ? "border-rose-400 bg-rose-500"
                                : "border-slate-200/70 bg-slate-900/80"
                            }`}
                          >
                            {isSelected && <span className="text-[10px] font-black text-white">✓</span>}
                          </div>
                        </div>
                      )}

                      <div
                        className={`absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent transition-opacity ${
                          isSelectionMode ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                        } flex flex-col justify-end p-3 gap-2`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSetPrimaryPhoto(photo);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-white"
                          >
                            <Star size={12} />
                            Primary
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDeleteConfirmation([photoUrl]);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-white"
                          >
                            <Trash2 size={12} />
                            Delete
                          </button>
                        </div>
                        <span className="text-white text-[10px] font-bold">
                          {isSelectionMode ? "Select to delete" : "Click to view"}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full py-20 text-center text-slate-500 font-medium">
                  No photos uploaded yet.
                </div>
              )}
            </div>

            {deleteModalOpen && (
              <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4">
                <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-950 p-5 shadow-2xl">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Confirm delete
                      </p>
                      <h3 className="mt-2 text-lg font-bold text-slate-100">
                        Delete photo{photosToDelete.length === 1 ? "" : "s"}?
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={closeDeleteConfirmation}
                      className="rounded-full p-1 text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <p className="mt-4 text-sm leading-6 text-slate-300">
                    {deleteModalMessage}
                  </p>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={closeDeleteConfirmation}
                      className="rounded-lg border border-slate-700 px-4 py-2 text-[10px] font-black uppercase tracking-[0.16em] text-slate-200"
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={handleDeletePhotos}
                      className="rounded-lg bg-rose-600 px-4 py-2 text-[10px] font-black uppercase tracking-[0.16em] text-white"
                    >
                      Yes, delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "biodata" && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden min-h-[700px] flex flex-col transition-all duration-300">
            <div className="p-4 border-b border-slate-800 bg-slate-800/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                  <span className="text-lg">📄</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-500 uppercase font-black tracking-widest">
                    Document Viewer
                  </span>
                  <span className="text-sm font-bold text-slate-200">
                    User Biodata (PDF)
                  </span>
                </div>
              </div>

              <label className="cursor-pointer group relative overflow-hidden px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black uppercase rounded-lg transition-all shadow-lg shadow-blue-600/20 active:scale-95 flex items-center gap-2">
                <Upload
                  size={14}
                  className="group-hover:-translate-y-0.5 transition-transform"
                />
                {biodata ? "Update Biodata" : "Upload Biodata"}
                <input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
            </div>

            {biodata ? (
              <div className="flex-1 bg-slate-900 flex flex-col">
                <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(biodata)}&embedded=true`}
                  className="w-full h-full min-h-[600px] border-none"
                  title="Biodata PDF"
                ></iframe>
                <div className="p-3 bg-slate-800/40 border-t border-slate-700/50 flex items-center justify-center">
                  <a
                    href={biodata}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-bold text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-widest flex items-center gap-2"
                  >
                    Open In New Tab
                    <ChevronRight size={10} />
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center mb-6 shadow-2xl">
                  <Upload size={32} className="text-slate-600 animate-pulse" />
                </div>
                <h4 className="text-slate-200 font-bold mb-2">
                  No Biodata Uploaded
                </h4>
                <p className="text-slate-500 text-xs max-w-[280px] leading-relaxed mb-8">
                  The administrator hasn't uploaded a biodata PDF for this user
                  yet. Click the button above to upload one now.
                </p>
                <div className="px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700/50">
                  <span className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em]">
                    Supported Formats: PDF <span className="mx-2">•</span> SIZE:
                    MAX 20MB
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "chat" && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden h-[820px] max-h-[calc(100vh-120px)] min-h-[620px] flex flex-col transition-all duration-300">
            <div className="p-4 border-b border-slate-800 bg-slate-800/20 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                  <MessageCircle size={16} className="text-emerald-400" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-500 uppercase font-black tracking-widest">
                    WhatsApp Chat
                  </span>
                  <span className="text-sm font-bold text-slate-200">
                    {whatsappChatData?.wa_id ? `+${whatsappChatData.wa_id}` : "Conversation"}
                  </span>
                </div>
              </div>
              {whatsappChatData?.total > 0 && (
                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-1.5">
                  {whatsappChatData.total} Messages
                </span>
              )}
            </div>

            <div
              ref={whatsappChatScrollRef}
              onScroll={handleWhatsappChatScroll}
              className="flex-1 min-h-0 overflow-y-auto bg-slate-950 px-4 py-6 custom-scrollbar"
            >
              {whatsappChatLoading ? (
                <div className="h-full min-h-[520px] flex flex-col items-center justify-center gap-3 text-slate-500">
                  <div className="w-9 h-9 rounded-full border-2 border-emerald-500/20 border-t-emerald-400 animate-spin" />
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                    Loading Chat
                  </span>
                </div>
              ) : whatsappMessages.length > 0 ? (
                <>
                  {whatsappOlderLoading && (
                    <div className="mb-3 flex justify-center">
                      <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-300">
                        Loading Older Messages
                      </span>
                    </div>
                  )}
                  <ChatWindow
                    messages={whatsappMessages}
                    previewImage={chatPreviewImage}
                    setPreviewImage={setChatPreviewImage}
                  />
                </>
              ) : (
                <div className="h-full min-h-[520px] flex flex-col items-center justify-center text-center px-6">
                  <div className="w-14 h-14 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center mb-4">
                    <MessageCircle size={22} className="text-slate-600" />
                  </div>
                  <h4 className="text-sm font-black text-slate-200 uppercase tracking-widest">
                    No WhatsApp Chat Found
                  </h4>
                  <p className="mt-2 text-xs text-slate-500 max-w-md leading-relaxed">
                    No messages were found for this user's saved phone number.
                  </p>
                  {whatsappChatData?.searched_wa_ids?.length > 0 && (
                    <p className="mt-3 text-[10px] text-slate-600 font-mono">
                      Checked: {whatsappChatData.searched_wa_ids.join(", ")}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className={whatsappMessageSending ? "opacity-60 pointer-events-none" : ""}>
              <ChatInput onSend={handleSendWhatsappChatMessage} />
            </div>
          </div>
        )}
      </div>

      {chatPreviewImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 backdrop-blur-xl"
          onClick={() => setChatPreviewImage(null)}
        >
          <img
            src={chatPreviewImage}
            alt="Chat media preview"
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 backdrop-blur-xl"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl w-full h-full flex flex-col items-center justify-center">
            <img
              src={selectedImage}
              alt="Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl animate-zoom-in"
            />
            <button className="mt-6 px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full font-bold transition-all">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualProfileView;
